import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { DISCIPLINE_ORDER } from '../../constants/disciplines';
import type { AgencyClinicResponse, OrgMemberResponse } from './types';
import {
  syncRowsToPendingSelection,
  type ClinicDisciplineRow,
} from './clinicDisciplineRows';

export type { ClinicDisciplineRow } from './clinicDisciplineRows';

type UseClinicDisciplinesOptions = {
  /** Live assignable-clinician clinic IDs from role selection; omit while roles load. */
  pendingAssignableClinicIds?: Set<string>;
  clinics?: AgencyClinicResponse[];
};

function buildSavedRows(
  member: OrgMemberResponse | null
): ClinicDisciplineRow[] {
  const assignments = member?.clinic_assignments ?? [];
  return assignments
    .filter(a => a.is_assignable_clinician)
    .map(a => ({
      clinic_id: a.clinic_id,
      clinic_name: a.clinic_name,
      disciplines: new Set(a.disciplines ?? []),
    }))
    .sort((a, b) => a.clinic_name.localeCompare(b.clinic_name));
}

const ALL_DISCIPLINES = DISCIPLINE_ORDER;

function rowsEqual(
  current: ClinicDisciplineRow[],
  initial: ClinicDisciplineRow[]
): boolean {
  if (current.length !== initial.length) return false;
  for (let i = 0; i < current.length; i += 1) {
    const a = current[i];
    const b = initial[i];
    if (a.clinic_id !== b.clinic_id) return false;
    if (a.disciplines.size !== b.disciplines.size) return false;
    for (const discipline of a.disciplines) {
      if (!b.disciplines.has(discipline)) return false;
    }
  }
  return true;
}

export function useClinicDisciplines(
  member: OrgMemberResponse | null,
  options?: UseClinicDisciplinesOptions
) {
  const { pendingAssignableClinicIds, clinics = [] } = options ?? {};
  const savedRows = useMemo(() => buildSavedRows(member), [member]);
  const [rows, setRows] = useState<ClinicDisciplineRow[]>(savedRows);

  useEffect(() => {
    if (pendingAssignableClinicIds === undefined) {
      setRows(savedRows);
      return;
    }

    setRows(prev =>
      syncRowsToPendingSelection(
        pendingAssignableClinicIds,
        clinics,
        member,
        prev
      )
    );
  }, [pendingAssignableClinicIds, clinics, member, savedRows]);

  const isDirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows]);

  const toggleDiscipline = useCallback(
    (clinicId: string, discipline: DisciplineType) => {
      setRows(prev =>
        prev.map(row => {
          if (row.clinic_id !== clinicId) return row;
          const next = new Set(row.disciplines);
          if (next.has(discipline)) {
            next.delete(discipline);
          } else {
            next.add(discipline);
          }
          return { ...row, disciplines: next };
        })
      );
    },
    []
  );

  const selectAllDisciplinesForClinic = useCallback((clinicId: string) => {
    setRows(prev =>
      prev.map(row =>
        row.clinic_id === clinicId
          ? { ...row, disciplines: new Set(ALL_DISCIPLINES) }
          : row
      )
    );
  }, []);

  const clearDisciplinesForClinic = useCallback((clinicId: string) => {
    setRows(prev =>
      prev.map(row =>
        row.clinic_id === clinicId ? { ...row, disciplines: new Set() } : row
      )
    );
  }, []);

  const selectAllDisciplines = useCallback(() => {
    setRows(prev =>
      prev.map(row => ({ ...row, disciplines: new Set(ALL_DISCIPLINES) }))
    );
  }, []);

  const clearAllDisciplines = useCallback(() => {
    setRows(prev => prev.map(row => ({ ...row, disciplines: new Set() })));
  }, []);

  const getAssignments = useCallback(
    () =>
      rows.map(row => ({
        clinic_id: row.clinic_id,
        disciplines: [...row.disciplines],
      })),
    [rows]
  );

  const reset = useCallback(() => {
    setRows(savedRows);
  }, [savedRows]);

  return {
    rows,
    isDirty,
    toggleDiscipline,
    selectAllDisciplinesForClinic,
    clearDisciplinesForClinic,
    selectAllDisciplines,
    clearAllDisciplines,
    getAssignments,
    reset,
  };
}
